import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Square } from 'lucide-react';
import { ColorEditorPopover } from './autoplay/ColorEditorPopover';
import { VideoFeatures, Border } from '@/store/videoFeatures/types';
import { HeroCard } from '@/components/ui/HeroCard';
import { SectionCard } from '@/components/ui/SectionCard';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface BorderConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const BorderConfig: React.FC<BorderConfigProps> = ({ features, setFeature }) => {
  useLanguage();
  const [enabled, setEnabled] = useState(features.border?.enabled ?? false);
  const [color, setColor] = useState(features.border?.color ?? 'rgba(255, 255, 255, 1)');
  const [width, setWidth] = useState(features.border?.width ?? 2);
  const [radius, setRadius] = useState(features.border?.radius ?? 8);
  const setBorderProperty = (updates: Partial<Border>) => {
    const updatedBorder: Border = {
      ...features.border,
      ...updates,
      enableBorder: updates.enabled !== undefined ? updates.enabled : features.border?.enabled,
      borderColor: updates.color ?? features.border?.color,
      borderWidth: updates.width ?? features.border?.width,
      borderRadius: updates.radius ?? features.border?.radius,
    };
    setFeature('border', updatedBorder);
  };

  const handleManualChange = (updates: Partial<Border>) => {
    setBorderProperty({ ...updates, stylePreset: 'custom' });
  };

  const handleToggle = (value: boolean) => {
    setEnabled(value);
    // Ao desativar a borda, zera também o arredondamento (volta ao padrão 0) —
    // senão o player arredondaria o vídeo mesmo sem borda.
    if (!value) {
      setRadius(0);
      setBorderProperty({ enabled: value, radius: 0 });
    } else {
      setBorderProperty({ enabled: value });
    }
  };

  useEffect(() => {
    setEnabled(features.border?.enabled ?? false);
    setColor(features.border?.color ?? 'rgba(255, 255, 255, 1)');
    setWidth(features.border?.width ?? 2);
    setRadius(features.border?.radius ?? 8);
  }, [features.border]);

  return (
    <div className="space-y-6 py-4 px-6">
      {/* HeroCard - Controle Mestre */}
      <HeroCard
        icon={<Square className="w-full h-full" strokeWidth={2} />}
        title={translateInterfaceText("Borda do Player")}
        description={translateInterfaceText("Destaque o player com uma borda elegante sem pesar no visual.")}
        isActive={enabled}
        onToggle={handleToggle}
      />

      {/* Conteúdo - Só aparece quando ativado */}
      {enabled && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-4"
        >
          {/* SectionCard - Aparência */}
          <SectionCard
            title={translateInterfaceText("Aparência")}
            description={translateInterfaceText("Cor, espessura e arredondamento da borda.")}
          >
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-[#374151]">{translateInterfaceText("Cor da borda")}</Label>
                <ColorEditorPopover 
                  colorName={translateInterfaceText("Borda")}
                  currentColor={color} 
                  onSave={(newColor) => { 
                    setColor(newColor); 
                    handleManualChange({ color: newColor }); 
                  }}
                >
                  <button type="button" className="flex items-center gap-2 cursor-pointer group">
                    <div 
                      className="w-8 h-8 rounded-full border border-[#E5E7EB] transition-all duration-200 group-hover:shadow-[0_0_0_3px_rgba(29,79,255,0.15)]" 
                      style={{ backgroundColor: color }} 
                    />
                    <span className="text-[11px] text-[#6B7280]">{translateInterfaceText("Clique para alterar")}</span>
                  </button>
                </ColorEditorPopover>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[12px] font-medium text-[#374151]">{translateInterfaceText("Espessura")}</Label>
                  <span className="text-[11px] text-[#6B7280]">{width}px</span>
                </div>
                <Slider 
                  value={[width]} 
                  onValueChange={([v]) => { setWidth(v); handleManualChange({ width: v }); }} 
                  min={0} 
                  max={12} 
                  step={1} 
                  className="w-full" 
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[12px] font-medium text-[#374151]">{translateInterfaceText("Arredondamento")}</Label>
                  <span className="text-[11px] text-[#6B7280]">{radius}px</span>
                </div>
                <Slider 
                  value={[radius]} 
                  onValueChange={([v]) => { setRadius(v); handleManualChange({ radius: v }); }} 
                  min={0} 
                  max={32} 
                  step={2} 
                  className="w-full" 
                />
              </div>
            </div>
          </SectionCard>

        </motion.div>
      )}
    </div>
  );
};
